import React, { useState } from 'react';
import {
  Card,
  Button,
  Upload,
  List,
  Typography,
  Space,
  Tag,
  Popconfirm,
  message,
  Empty,
  Tooltip,
  Input,
  Modal,
  Select,
  Checkbox,
  Divider,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileOutlined,
  InfoCircleOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';
import dayjs from 'dayjs';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

export default function KnowledgeBaseTab({ config }) {
  const { t } = useTranslation(['tables']);
  const queryClient = useQueryClient();
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [fileDescription, setFileDescription] = useState('');
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [selectedSourceConfigId, setSelectedSourceConfigId] = useState(null);
  const [selectedFileIds, setSelectedFileIds] = useState([]);

  const pa = (key) => t(`payrollAutomation.agentConfig.${key}`, { ns: 'tables', defaultValue: key });

  // Fetch all configurations for import
  const { data: allConfigsData } = useQuery({
    queryKey: ['agent-configs', config?.type],
    queryFn: () => API.agentConfig.getAgentConfigurations({ type: config?.type }),
    enabled: !!config?.type && importModalVisible,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (formData) => API.payrollAutomation.uploadReferenceFile(config._id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['agent-config-detail', config._id]);
      message.success(pa('referenceFileUploaded') || 'Reference file uploaded successfully');
      setUploadModalVisible(false);
      setFileToUpload(null);
      setFileDescription('');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to upload file');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (fileId) => API.payrollAutomation.deleteReferenceFile(config._id, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries(['agent-config-detail', config._id]);
      message.success(pa('referenceFileDeleted') || 'Reference file deleted successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to delete file');
    },
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: ({ sourceConfigId, fileIds }) =>
      API.agentConfig.importReferenceFiles(config._id, sourceConfigId, fileIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['agent-config-detail', config._id]);
      message.success(
        pa('filesImported') || `${data.data.importedCount} file(s) imported successfully`
      );
      setImportModalVisible(false);
      setSelectedSourceConfigId(null);
      setSelectedFileIds([]);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to import files');
    },
  });

  const handleUpload = () => {
    if (!fileToUpload) {
      message.error(pa('selectFile') || 'Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', fileToUpload);
    if (fileDescription) {
      formData.append('description', fileDescription);
    }

    uploadMutation.mutate(formData);
  };

  const handleImport = () => {
    if (!selectedSourceConfigId) {
      message.error(pa('selectSourceConfig') || 'Please select a source configuration');
      return;
    }
    if (selectedFileIds.length === 0) {
      message.error(pa('selectFilesToImport') || 'Please select at least one file to import');
      return;
    }

    importMutation.mutate({
      sourceConfigId: selectedSourceConfigId,
      fileIds: selectedFileIds,
    });
  };

  const handleSourceConfigChange = (configId) => {
    setSelectedSourceConfigId(configId);
    setSelectedFileIds([]); // Reset selected files when changing source
  };

  const getFileIcon = (mimeType, filename) => {
    if (mimeType.includes('pdf')) return <FilePdfOutlined style={{ color: '#f5222d' }} />;
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || filename.match(/\.xlsx?$/i)) {
      return <FileExcelOutlined style={{ color: '#52c41a' }} />;
    }
    if (mimeType.includes('word') || mimeType.includes('document') || filename.match(/\.docx?$/i)) {
      return <FileWordOutlined style={{ color: '#1890ff' }} />;
    }
    if (mimeType.includes('text') || filename.match(/\.(md|txt|json|xml)$/i)) {
      return <FileTextOutlined style={{ color: '#13c2c2' }} />;
    }
    return <FileOutlined />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const referenceFiles = config?.referenceFiles || [];

  if (!config) {
    return (
      <Card>
        <Empty description={pa('noConfigSelected') || 'No configuration selected'} />
      </Card>
    );
  }

  return (
    <div>
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Title level={4} style={{ margin: 0 }}>
                {pa('knowledgeBase') || 'Knowledge Base'}
              </Title>
              <Tooltip title={pa('knowledgeBaseTooltip') || 'Upload reference files to help Claude understand your data better'}>
                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip>
            </Space>
            <Space>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => setUploadModalVisible(true)}
                disabled={config.active}
              >
                {pa('uploadFile') || 'Upload File'}
              </Button>
              <Button
                icon={<ImportOutlined />}
                onClick={() => setImportModalVisible(true)}
                disabled={config.active}
              >
                {pa('importFiles') || 'Import'}
              </Button>
            </Space>
          </div>
        }
      >
        <Paragraph type="secondary">
          {pa('knowledgeBaseDescription') || 'Upload reference documents (.md, .txt, .json, .xml, .pdf, .xlsx, .docx) that Claude will use as context when parsing files. These files help improve accuracy and provide additional guidance.'}
        </Paragraph>

        {config.active && (
          <div style={{ marginBottom: 16 }}>
            <Tag color="warning" icon={<InfoCircleOutlined />}>
              {pa('cannotModifyActive') || 'Cannot modify active configuration. Deactivate first or create new version.'}
            </Tag>
          </div>
        )}

        {referenceFiles.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={pa('noReferenceFiles') || 'No reference files uploaded yet'}
          />
        ) : (
          <List
            dataSource={referenceFiles}
            renderItem={(file) => (
              <List.Item
                actions={[
                  <Popconfirm
                    key="delete"
                    title={pa('deleteFileConfirm') || 'Delete this file?'}
                    onConfirm={() => deleteMutation.mutate(file._id)}
                    okText={pa('yes') || 'Yes'}
                    cancelText={pa('no') || 'No'}
                    disabled={config.active}
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      loading={deleteMutation.isPending}
                      disabled={config.active}
                    >
                      {pa('delete') || 'Delete'}
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={getFileIcon(file.mimeType, file.originalName)}
                  title={
                    <Space>
                      <Text strong>{file.originalName}</Text>
                      <Tag>{formatFileSize(file.size)}</Tag>
                      {file.content && <Tag color="blue">In DB</Tag>}
                      {file.filePath && <Tag color="green">On Disk</Tag>}
                    </Space>
                  }
                  description={
                    <div>
                      {file.description && (
                        <Paragraph type="secondary" style={{ marginBottom: 4 }}>
                          {file.description}
                        </Paragraph>
                      )}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Uploaded: {dayjs(file.uploadedAt).format('DD.MM.YYYY HH:mm')}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* Upload Modal */}
      <Modal
        title={pa('uploadReferenceFile') || 'Upload Reference File'}
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setFileToUpload(null);
          setFileDescription('');
        }}
        onOk={handleUpload}
        confirmLoading={uploadMutation.isPending}
        okText={pa('upload') || 'Upload'}
        cancelText={pa('cancel') || 'Cancel'}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>{pa('selectFile') || 'Select File'}</Text>
            <div style={{ marginTop: 8 }}>
              <Upload
                beforeUpload={(file) => {
                  setFileToUpload(file);
                  return false;
                }}
                onRemove={() => setFileToUpload(null)}
                fileList={fileToUpload ? [fileToUpload] : []}
                maxCount={1}
                accept=".md,.txt,.json,.xml,.py,.pdf,.xls,.xlsx,.docx,.doc"
              >
                <Button icon={<UploadOutlined />}>
                  {pa('chooseFile') || 'Choose File'}
                </Button>
              </Upload>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Allowed: .md, .txt, .json, .xml, .py, .pdf, .xlsx, .docx
            </Text>
          </div>

          <div>
            <Text strong>{pa('description') || 'Description'} (Optional)</Text>
            <TextArea
              rows={3}
              value={fileDescription}
              onChange={(e) => setFileDescription(e.target.value)}
              placeholder={pa('descriptionPlaceholder') || 'Describe the purpose of this file...'}
              style={{ marginTop: 8 }}
            />
          </div>
        </Space>
      </Modal>

      {/* Import Modal */}
      <Modal
        title={pa('importReferenceFiles') || 'Import Reference Files'}
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setSelectedSourceConfigId(null);
          setSelectedFileIds([]);
        }}
        onOk={handleImport}
        confirmLoading={importMutation.isPending}
        okText={pa('import') || 'Import'}
        cancelText={pa('cancel') || 'Cancel'}
        width={700}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>{pa('selectSourceConfig') || 'Select Source Configuration'}</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder={pa('selectConfigPlaceholder') || 'Choose a configuration to import files from'}
              value={selectedSourceConfigId}
              onChange={handleSourceConfigChange}
              options={
                allConfigsData?.data
                  ?.filter((c) => c._id !== config._id) // Exclude current config
                  ?.map((c) => ({
                    label: `${c.name} (v${c.version}) - ${c.referenceFiles?.length || 0} files`,
                    value: c._id,
                  })) || []
              }
            />
          </div>

          {selectedSourceConfigId && (() => {
            const sourceConfig = allConfigsData?.data?.find((c) => c._id === selectedSourceConfigId);
            const sourceFiles = sourceConfig?.referenceFiles || [];

            if (sourceFiles.length === 0) {
              return (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={pa('noFilesInSource') || 'No files in selected configuration'}
                />
              );
            }

            return (
              <div>
                <Text strong>{pa('selectFilesToImport') || 'Select Files to Import'}</Text>
                <Divider style={{ margin: '8px 0' }} />
                <Checkbox.Group
                  style={{ width: '100%' }}
                  value={selectedFileIds}
                  onChange={setSelectedFileIds}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {sourceFiles.map((file) => (
                      <Checkbox key={file._id} value={file._id}>
                        <Space>
                          {getFileIcon(file.mimeType, file.originalName)}
                          <Text>{file.originalName}</Text>
                          <Tag>{formatFileSize(file.size)}</Tag>
                          {file.description && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              - {file.description}
                            </Text>
                          )}
                        </Space>
                      </Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
              </div>
            );
          })()}
        </Space>
      </Modal>
    </div>
  );
}
